import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { formatDate, formatGBP, daysUntil } from "../lib/format";
import { Search, ExternalLink, Calendar, Clock, AlertTriangle, XCircle } from "lucide-react";

const STATUS_TABS = [
  { id: "open", label: "Open", icon: Calendar, hint: "Accepting applications" },
  { id: "closing_soon", label: "Closing soon", icon: Clock, hint: "Deadline within 30 days" },
  { id: "rolling", label: "Rolling / no date", icon: AlertTriangle, hint: "No fixed deadline" },
  { id: "closed", label: "Closed", icon: XCircle, hint: "Deadline passed" },
];

export default function Funding() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ open: 0, closing_soon: 0, rolling: 0, closed: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("open");
  const [sort, setSort] = useState("deadline_asc");
  const [loading, setLoading] = useState(true);

  // Pre-fetch counts across status buckets so the tabs always show the right number.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/funding/counts");
        if (cancel) return;
        setCounts(data || {});
      } catch (_) { /* counts are best-effort */ }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/funding", { params: { q: q || undefined, status, sort, limit: 250 } })
        .then((r) => setItems(r.data || []))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q, status, sort]);

  return (
    <div data-testid="funding-page">
      <div className="mb-6">
        <div className="label-meta">R&amp;D / innovation</div>
        <div className="display">Funding opportunities</div>
        <p className="text-[13px] mt-1" style={{ color: "var(--parapet-text-secondary)" }}>
          Live EU Defence Fund, UKRI, Innovate UK and DASA calls — refreshed daily and filtered
          to forthcoming / open status only. Closed entries kept here for reference.
        </p>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="funding-status-tabs">
        {STATUS_TABS.map((t) => {
          const Icon = t.icon;
          const active = status === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setStatus(t.id)}
              className="filter-chip"
              style={{
                background: active ? "#111" : "#fff",
                color: active ? "#fff" : "#111",
                borderColor: active ? "#111" : "var(--parapet-border)",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
              title={t.hint}
              data-testid={`funding-tab-${t.id}`}
            >
              <Icon size={12} />
              <span>{t.label}</span>
              <span className="value-mono text-[11px]" style={{ opacity: 0.7 }}>
                {counts[t.id] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      <div className="p-card-tight mb-4">
        <div className="grid gap-2" style={{ gridTemplateColumns: "1fr 200px" }}>
          <div className="relative">
            <Search size={14} style={{ position: "absolute", left: 10, top: 11, color: "var(--parapet-text-secondary)" }} />
            <input
              className="p-input w-full" style={{ paddingLeft: 32 }}
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search funding calls…" data-testid="funding-search"
            />
          </div>
          <select
            className="p-input"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="funding-sort"
          >
            <option value="deadline_asc">Deadline soonest</option>
            <option value="deadline_desc">Deadline latest</option>
            <option value="posted_desc">Recently posted</option>
          </select>
        </div>
      </div>

      <div className="p-card" style={{ padding: 0 }}>
        {loading ? (
          <div className="p-6 text-center"><span className="spinner" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state m-6" data-testid="funding-empty">
            <h3>No {status === "open" ? "open" : status.replace("_", " ")} calls right now</h3>
            <p>
              Try a different status above, or wait for tomorrow&apos;s ingestion cycle to pick up
              new EU / UKRI / Innovate UK calls.
            </p>
          </div>
        ) : (
          <table className="p-table">
            <thead>
              <tr>
                <th>Title</th>
                <th style={{ width: 160 }}>Funder</th>
                <th style={{ width: 150 }}>Value</th>
                <th style={{ width: 150 }}>Deadline / posted</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <FundingRow key={f.id || f.source_url || f.title} f={f} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function FundingRow({ f }) {
  const d = daysUntil(f.deadline);
  const value = useMemo(() => {
    if (f.amount_max && f.amount_min) {
      return `${formatGBP(f.amount_min, f.currency)} – ${formatGBP(f.amount_max, f.currency)}`;
    }
    if (f.amount_max) return formatGBP(f.amount_max, f.currency);
    if (f.amount_min) return `≥ ${formatGBP(f.amount_min, f.currency)}`;
    return null;
  }, [f.amount_min, f.amount_max, f.currency]);

  return (
    <tr data-testid={`funding-row-${f.id || f.title}`}>
      <td>
        <div className="font-medium text-[13px]">{f.title}</div>
        {f.description && (
          <div className="text-[12px] mt-1" style={{ color: "var(--parapet-text-secondary)" }}>
            {f.description.length > 180 ? f.description.slice(0, 180) + "…" : f.description}
          </div>
        )}
        <div className="flex gap-1 mt-2 flex-wrap">
          <SourcePill source={f.source} />
          <StatusPill status={f.status} />
          {f.programme && <span className="source-tag">{f.programme}</span>}
        </div>
      </td>
      <td className="text-[12px]">{f.funder || "—"}</td>
      <td className="value-mono text-[13px]">
        {value || <span style={{ color: "var(--parapet-text-secondary)" }}>—</span>}
      </td>
      <td>
        {f.deadline ? (
          <div>
            <div className="text-[12px]">{formatDate(f.deadline)}</div>
            {d != null && (
              <div className="text-[11px]" style={{ color: d < 0 ? "#7a0606" : "var(--parapet-text-secondary)" }}>
                {d > 0 ? `${d}d remaining` : d === 0 ? "Closes today" : `Closed ${Math.abs(d)}d ago`}
              </div>
            )}
          </div>
        ) : f.posted_at ? (
          <div>
            <div className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>No deadline</div>
            <div className="text-[11px]" style={{ color: "var(--parapet-text-secondary)" }}>
              Posted {formatDate(f.posted_at)}
            </div>
          </div>
        ) : (
          <span className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>—</span>
        )}
      </td>
      <td>
        {f.source_url && (
          <a
            href={f.source_url}
            target="_blank"
            rel="noreferrer"
            className="btn-ghost text-[12px]"
            data-testid={`funding-open-${f.id || f.title}`}
          >
            <ExternalLink size={11} /> Open
          </a>
        )}
      </td>
    </tr>
  );
}

const SOURCE_LABELS = {
  eu_funding_tenders: "EU Funding",
  ukri: "UKRI",
  innovate_uk_smart_grants: "Innovate UK",
  dasa_calls: "DASA",
};

function SourcePill({ source }) {
  return <span className="source-tag">{SOURCE_LABELS[source] || (source || "—").replace(/_/g, " ")}</span>;
}

const STATUS_STYLE = {
  open:         { bg: "#E7F5EB", fg: "#0a6b2a", label: "Open" },
  closing_soon: { bg: "#FFF6E5", fg: "#7a4a02", label: "Closing soon" },
  rolling:      { bg: "#EEF3FF", fg: "#1A4FB3", label: "Rolling" },
  closed:       { bg: "#FDECEC", fg: "#7a0606", label: "Closed" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || { bg: "#F4F4F4", fg: "#111", label: status };
  return (
    <span
      className="value-mono text-[11px]"
      style={{ background: s.bg, color: s.fg, padding: "2px 7px", borderRadius: 3, letterSpacing: 0.3 }}
    >
      {s.label}
    </span>
  );
}
